import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CategoryAutocompleteProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export const CategoryAutocomplete = ({ value, onChange }: CategoryAutocompleteProps) => {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .order('name');
      
      if (error) {
        toast.error('حدث خطأ أثناء تحميل التصنيفات');
        return [];
      }
      
      return data.map(category => category.name);
    },
  });

  const handleValueChange = (category: string) => {
    const newValue = value.includes(category)
      ? value.filter((v) => v !== category)
      : [...value, category];
    onChange(newValue);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-10 bg-muted rounded-md">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Select
        onValueChange={handleValueChange}
        value={value[value.length - 1] || ""}
      >
        <SelectTrigger className="w-full text-right bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <SelectValue placeholder="اختر التصنيفات..." />
        </SelectTrigger>
        <SelectContent 
          align="end"
          className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg"
        >
          <ScrollArea className="h-[200px]">
            {categories.map((category) => (
              <SelectItem
                key={category}
                value={category}
                className="text-right flex items-center justify-between cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/20"
              >
                <span className="flex items-center gap-2">
                  <Check
                    className={`h-4 w-4 ${
                      value.includes(category) ? "opacity-100 text-primary" : "opacity-0"
                    }`}
                  />
                  {category}
                </span>
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      {/* Display selected categories as badges */}
      <div className="flex flex-wrap gap-2 mt-2">
        {value.map((category) => (
          <Badge
            key={category}
            variant="secondary"
            className="bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer"
            onClick={() => handleValueChange(category)}
          >
            {category}
            <span className="mr-1 opacity-70">×</span>
          </Badge>
        ))}
      </div>
    </div>
  );
};